import React, { useRef, useEffect } from 'react';
import './DashedLine.css'; // Ensure CSS includes mask-mode: alpha

function DashedLine({ coords }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        // 1. Get current element and viewport dimensions
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const elementTopPx = rect.top; // Pixels from viewport top to element top
        const elementHeight = rect.height; // Height of the element in pixels

        if (elementHeight <= 0 || viewportHeight <= 0) {
          const transparentMask = 'linear-gradient(to bottom, transparent 0%, transparent 100%)';
          containerRef.current.style.maskImage = transparentMask;
          containerRef.current.style.WebkitMaskImage = transparentMask;
          return;
        }

        // 2. Define Viewport Fade/Opaque Zones (Relative 0.0 to 1.0)
        //    Adjust these values to control the fade steepness and opaque area size.
        //    Example: 0.2/0.8 means 20% fade at top, 60% opaque middle, 20% fade at bottom.
        const fadeStartRel = 0.20; // Opacity reaches 100% at this point (e.g., 20% down viewport)
        const fadeEndRel = 0.80;   // Opacity starts dropping from 100% at this point (e.g., 80% down viewport)
        // ---

        // 3. Calculate the four key viewport pixel coordinates
        const vpPoint1 = 0; // Absolute top of viewport (opacity 0%)
        const vpPoint2 = viewportHeight * fadeStartRel; // End of top fade (opacity 100%)
        const vpPoint3 = viewportHeight * fadeEndRel;   // Start of bottom fade (opacity 100%)
        const vpPoint4 = viewportHeight; // Absolute bottom of viewport (opacity 0%)

        // 4. Map these viewport pixel coordinates onto the element's height scale (0% to 100%)
        //    We calculate where each viewport point falls relative to the element's top,
        //    then convert that pixel distance to a percentage of the element's height.
        //    Clamp the result between 0% and 100%.

        const calculatePercent = (vpPointPx) => {
          const pointPxOnElement = vpPointPx - elementTopPx;
          const percent = (pointPxOnElement / elementHeight) * 100;
          return Math.max(0, Math.min(100, percent)); // Clamp
        };

        const elementPercent1 = calculatePercent(vpPoint1); // Maps to viewport 0% Vh
        const elementPercent2 = calculatePercent(vpPoint2); // Maps to viewport fadeStartRel Vh
        const elementPercent3 = calculatePercent(vpPoint3); // Maps to viewport fadeEndRel Vh
        const elementPercent4 = calculatePercent(vpPoint4); // Maps to viewport 100% Vh

        // 5. Construct the CSS gradient string for the mask
        //    - Transparent from element top up to the point mapping to vpPoint1
        //    - Fades to Black between the points mapping to vpPoint1 and vpPoint2
        //    - Stays Black between the points mapping to vpPoint2 and vpPoint3
        //    - Fades to Transparent between the points mapping to vpPoint3 and vpPoint4
        //    - Stays Transparent from the point mapping to vpPoint4 to element bottom

        //    Note: We use the clamped percentages directly. The gradient smoothly interpolates.
        const maskGradient = `linear-gradient(to bottom,
            transparent ${elementPercent1.toFixed(2)}%,
            black ${elementPercent2.toFixed(2)}%,
            black ${elementPercent3.toFixed(2)}%,
            transparent ${elementPercent4.toFixed(2)}%
        )`;


        // 6. Apply the dynamic mask style to the element
        containerRef.current.style.maskImage = maskGradient;
        containerRef.current.style.WebkitMaskImage = maskGradient;

        // --- Optional Debugging ---
        /*
        console.clear();
        console.log(`Viewport Fade Zone Rel: ${fadeStartRel.toFixed(2)} -> ${fadeEndRel.toFixed(2)}`);
        console.log(`Element Top Px: ${elementTopPx.toFixed(1)}, Height Px: ${elementHeight.toFixed(1)}`);
        console.log(`Mapped %: p1=${elementPercent1.toFixed(1)}, p2=${elementPercent2.toFixed(1)}, p3=${elementPercent3.toFixed(1)}, p4=${elementPercent4.toFixed(1)}`);
        console.log(`Applied Mask: ${maskGradient}`);
        console.log("---------------------");
        */
      }
    };

    // Run initially and add listener
    const initialTimeoutId = setTimeout(handleScroll, 50);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      clearTimeout(initialTimeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Runs once on mount


  // --- SVG Rendering (Keep as before) ---
  if (!coords || coords.length < 2) { return null; }
  const svgLines = coords.slice(0, -1).map((startCoord, i) => {
    const endCoord = coords[i + 1]; const [x1, y1] = startCoord; const [x2, y2] = endCoord;
    return ( <line key={`line-${i}`} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`} className="dashed-line-segment-svg"/> );
  });
  return (
    <div ref={containerRef} className="dashed-line-container">
      <svg width="100%" height="100%" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0 }}> {svgLines} </svg>
    </div>
  );
}

export default DashedLine;